// Importing recorded activities: single GPX, TCX or FIT files (also gzipped) and Strava's account export
// (a ZIP with activities.csv and an activities/ folder). Everything runs on the device: files are parsed
// here and only the summary is saved (plus the route, if the person opts in). No Strava API (decision log).
import type { CardioSport } from '../types';
import {
  DEFAULT_MAX_HR, downsampleRoute, elevationProfile, sportFromFile, summarizeTrack, trackSplits,
  type CardioActivity, type ParsedTrack,
} from './activity';
import { parseFit } from './fit';
import { parseGpx, TrackFileError } from './gpx';
import { sportVariant } from './sports';
import { parseTcx } from './tcx';
import { gunzip, MAX_UNPACKED_BYTES, MAX_ZIP_TOTAL_BYTES, zipEntries, zipRead, type BlobLike, type ZipEntry } from './zip';

/** Reads a track file by its name: .gpx, .tcx, .fit, each optionally .gz. */
export async function parseTrackFile(name: string, bytes: Uint8Array): Promise<ParsedTrack> {
  let n = name.toLowerCase();
  let data = bytes;
  if (n.endsWith('.gz')) {
    data = await gunzip(bytes);
    n = n.slice(0, -3);
  }
  if (n.endsWith('.fit')) return parseFit(data);
  const text = new TextDecoder().decode(data);
  if (n.endsWith('.gpx')) return parseGpx(text);
  if (n.endsWith('.tcx')) return parseTcx(text.trimStart());
  throw new TrackFileError(`${name}: only GPX, TCX and FIT files can be imported.`);
}

/** One activity found in the files, before the person confirms it. */
export interface ActivityCandidate {
  /** Where it came from: the file name (or "activities.csv" for a Strava entry without a file). */
  file: string;
  name: string;
  startedAt: number;
  /** Null when the sport could not be told; the person picks one, or it is left out. */
  activity: CardioActivity | null;
  /** The sport word in the file or the Strava type, for the person to see. */
  sportWord?: string;
  /** Why it cannot be imported (not cardio, no time), or null. */
  problem: string | null;
  /** Same start time and distance as an activity already in the history. */
  duplicate?: boolean;
  /**
   * The file does not say which sport it is: `activity` holds the numbers with running as a placeholder, and
   * the person picks the sport before it can be saved (withSport()).
   */
  sportUnknown?: boolean;
  /** Not an activity but a message about the whole import (activities.csv in an unknown language). */
  notice?: boolean;
}

/** The candidate with the sport the person picked. */
export function withSport(c: ActivityCandidate, sport: CardioSport, variant?: string): ActivityCandidate {
  if (!c.activity) return c;
  return { ...c, sportUnknown: false, activity: { ...c.activity, sport, variant: sportVariant(sport, variant) } };
}

export interface FromTrackOptions {
  maxHr?: number;
  /** Keep the route points (opted in by the person). */
  keepRoute?: boolean;
  /** The sport when known from elsewhere (Strava's type); else from the file. */
  sport?: { sport: CardioSport; variant: string } | null;
  source: CardioActivity['source'];
}

/**
 * The activity of a parsed track: summary, splits, elevation profile, and the route only when kept. Without a
 * sport (neither given nor named in the file) the numbers come back with running as a placeholder and
 * `sportUnknown`, for the person to pick.
 */
export function activityFromTrack(track: ParsedTrack, opts: FromTrackOptions): { activity: CardioActivity | null; startedAt: number | null; problem: string | null; sportUnknown?: boolean } {
  const sum = summarizeTrack(track, opts.maxHr ?? DEFAULT_MAX_HR);
  const named = opts.sport ?? sportFromFile(track.sportHint, track.subSportHint);
  const sport = named ?? { sport: 'running' as CardioSport, variant: 'road' };
  if (sum.durationSec <= 0) return { activity: null, startedAt: sum.startedAt, problem: 'no times in the file' };
  const splits = trackSplits(track.samples);
  const profile = elevationProfile(track.samples);
  const route = opts.keepRoute ? downsampleRoute(track.samples) : [];
  const activity: CardioActivity = {
    v: 1, sport: sport.sport, variant: sportVariant(sport.sport, sport.variant), durationSec: sum.durationSec,
    ...(sum.elapsedSec > 0 ? { elapsedSec: sum.elapsedSec } : {}),
    ...(sum.distanceM > 0 ? { distanceM: sum.distanceM } : {}),
    ...(sum.track.hasElevation ? { elevationGainM: sum.elevationGainM, elevationLossM: sum.elevationLossM } : {}),
    ...(sum.avgHr !== undefined ? { avgHr: sum.avgHr } : {}),
    ...(sum.maxHr !== undefined ? { maxHr: sum.maxHr } : {}),
    ...(sum.zoneSec ? { zoneSec: sum.zoneSec } : {}),
    ...(sum.avgCadence !== undefined ? { avgCadence: sum.avgCadence } : {}),
    ...(track.totals?.poolLengthM ? { poolLengthM: track.totals.poolLengthM } : {}),
    source: opts.source,
    track: sum.track,
    ...(splits.length ? { splits } : {}),
    ...(profile.length ? { profile } : {}),
    ...(route.length ? { route } : {}),
  };
  return { activity, startedAt: sum.startedAt, problem: null, ...(named ? {} : { sportUnknown: true }) };
}

// Strava -------------------------------------------------------------------------------------------

/**
 * Strava's activity types (the app and export names, and the API's sport_type spelling), to our sports.
 * Types not listed (Weight Training, Yoga, Workout, Alpine Ski, Kayaking ...) are not a sport this app
 * tracks yet. Checked against Strava's sport type list (developers.strava.com, SportType), 2026-10.
 */
export const STRAVA_TYPES: Record<string, { sport: CardioSport; variant?: string }> = {
  'run': { sport: 'running', variant: 'road' },
  'trail run': { sport: 'running', variant: 'trail' },
  'virtual run': { sport: 'running', variant: 'treadmill' },
  'walk': { sport: 'walking', variant: 'outdoor' },
  'hike': { sport: 'hiking', variant: 'trail' },
  'snowshoe': { sport: 'hiking', variant: 'mountain' },
  'ride': { sport: 'cycling', variant: 'road' },
  'gravel ride': { sport: 'cycling', variant: 'gravel' },
  'virtual ride': { sport: 'cycling', variant: 'indoor' },
  'e bike ride': { sport: 'cycling', variant: 'road' },
  'velomobile': { sport: 'cycling', variant: 'road' },
  'handcycle': { sport: 'cycling', variant: 'road' },
  'mountain bike ride': { sport: 'mountain_biking', variant: 'trail' },
  'e mountain bike ride': { sport: 'mountain_biking', variant: 'trail' },
  'swim': { sport: 'swimming' },
  'rowing': { sport: 'rowing', variant: 'water' },
  'virtual row': { sport: 'rowing', variant: 'machine' },
  'nordic ski': { sport: 'skiing', variant: 'classic' },
  'roller ski': { sport: 'skiing', variant: 'skate' },
  'backcountry ski': { sport: 'skiing', variant: 'touring' },
  'inline skate': { sport: 'inline_skating' },
  'elliptical': { sport: 'elliptical' },
  'stair stepper': { sport: 'stair_climbing', variant: 'machine' },
};

/**
 * Strava's type names when the account is set to German, French or Italian (the export uses the account's
 * language), accents folded. Best effort: Strava's own translations were not available to test against;
 * TYPE_WORDS below catch variants.
 */
const LOCAL_TYPES: Record<string, [CardioSport, string?]> = {
  // German
  'lauf': ['running', 'road'], 'laufen': ['running', 'road'], 'traillauf': ['running', 'trail'], 'trailrun': ['running', 'trail'],
  'virtueller lauf': ['running', 'treadmill'], 'radfahrt': ['cycling', 'road'], 'fahrradfahrt': ['cycling', 'road'],
  'e bike fahrt': ['cycling', 'road'], 'virtuelle radfahrt': ['cycling', 'indoor'], 'gravel fahrt': ['cycling', 'gravel'],
  'mountainbike fahrt': ['mountain_biking', 'trail'], 'e mountainbike fahrt': ['mountain_biking', 'trail'],
  'schwimmen': ['swimming'], 'wanderung': ['hiking', 'trail'], 'bergwanderung': ['hiking', 'mountain'], 'spaziergang': ['walking', 'outdoor'],
  'gehen': ['walking', 'outdoor'], 'rudern': ['rowing', 'water'], 'virtuelles rudern': ['rowing', 'machine'],
  'langlauf': ['skiing', 'classic'], 'skilanglauf': ['skiing', 'classic'], 'skitour': ['skiing', 'touring'], 'skitouren': ['skiing', 'touring'],
  'rollski': ['skiing', 'skate'], 'inlineskaten': ['inline_skating'], 'inline skaten': ['inline_skating'], 'crosstrainer': ['elliptical'],
  'stepper': ['stair_climbing', 'machine'], 'treppensteigen': ['stair_climbing', 'stairs'], 'schneeschuhwandern': ['hiking', 'mountain'],
  // French
  'course a pied': ['running', 'road'], 'course': ['running', 'road'], 'course virtuelle': ['running', 'treadmill'], 'trail': ['running', 'trail'],
  'sortie a velo': ['cycling', 'road'], 'velo': ['cycling', 'road'], 'velo virtuel': ['cycling', 'indoor'], 'sortie velo virtuelle': ['cycling', 'indoor'],
  'velo electrique': ['cycling', 'road'], 'vtt': ['mountain_biking', 'trail'], 'velo de montagne': ['mountain_biking', 'trail'],
  'sortie gravel': ['cycling', 'gravel'], 'natation': ['swimming'], 'randonnee': ['hiking', 'trail'], 'marche': ['walking', 'outdoor'],
  'aviron': ['rowing', 'water'], 'ski de fond': ['skiing', 'classic'], 'ski de randonnee': ['skiing', 'touring'], 'ski a roulettes': ['skiing', 'skate'],
  'roller': ['inline_skating'], 'elliptique': ['elliptical'], 'raquettes': ['hiking', 'mountain'], 'raquette a neige': ['hiking', 'mountain'],
  // Italian
  'corsa': ['running', 'road'], 'corsa virtuale': ['running', 'treadmill'], 'trail running': ['running', 'trail'], 'giro in bici': ['cycling', 'road'],
  'bici': ['cycling', 'road'], 'ciclismo': ['cycling', 'road'], 'pedalata virtuale': ['cycling', 'indoor'], 'giro in bici virtuale': ['cycling', 'indoor'],
  'mountain bike': ['mountain_biking', 'trail'], 'giro in mountain bike': ['mountain_biking', 'trail'], 'nuoto': ['swimming'],
  'escursione': ['hiking', 'trail'], 'escursionismo': ['hiking', 'trail'], 'camminata': ['walking', 'outdoor'], 'canottaggio': ['rowing', 'water'],
  'sci di fondo': ['skiing', 'classic'], 'scialpinismo': ['skiing', 'touring'], 'skiroll': ['skiing', 'skate'], 'pattinaggio in linea': ['inline_skating'],
  'ellittica': ['elliptical'], 'racchette da neve': ['hiking', 'mountain'], 'ciaspolata': ['hiking', 'mountain'],
};

/**
 * Whole-word rules for names not in the tables, specific before general: snowshoe and ski touring before
 * cross-country skiing before hiking before virtual runs before running ("Wanderung" is not a run, "Langlauf"
 * is not a "Lauf", "Virtueller Lauf" is not a ride).
 */
const TYPE_WORDS: Array<[RegExp, CardioSport, string?]> = [
  [/\b(schneeschuh\w*|raquettes?|ciaspol\w*|snowshoe\w*)\b/, 'hiking', 'mountain'],
  [/\b(skitour\w*|scialpinismo|backcountry)\b|ski de randonnee/, 'skiing', 'touring'],
  [/\b(rollski|skiroll|roller ski)\b|ski a roulettes/, 'skiing', 'skate'],
  [/\b(\w*langlauf|nordic)\b|ski de fond|sci di fondo/, 'skiing', 'classic'],
  [/\b(\w*wanderung|wandern|randonnee|escursion\w*|hike|hiking)\b/, 'hiking', 'trail'],
  [/\b(spaziergang|gehen|marche|camminata|walk|walking)\b/, 'walking', 'outdoor'],
  [/\bvirtuell\w* lauf\b|\bcourse virtuelle\b|\bcorsa virtuale\b|\bvirtual run\b|\blaufband\b|\btreadmill\b/, 'running', 'treadmill'],
  [/\b(mountainbike\w*|mountain bike|mtb|vtt)\b|velo de montagne/, 'mountain_biking', 'trail'],
  [/\bgravel\w*/, 'cycling', 'gravel'],
  [/\bvirtuell\w* radfahrt\b|\bvelo virtuel\b|\bpedalata virtuale\b|\bindoor\b|\brollentrainer\b|\bhome trainer\b|\bvirtual ride\b/, 'cycling', 'indoor'],
  [/\b(\w*trail\w*)\b/, 'running', 'trail'],
  [/\b(lauf|laufen|course a pied|corsa|run|running|jogging)\b/, 'running', 'road'],
  [/\b(\w*radfahrt|fahrrad\w*|rad|velo\w*|bici\w*|cicl\w*|ride|cycling)\b/, 'cycling', 'road'],
  [/\b(schwimm\w*|natation|nuoto|nage|swim\w*)\b/, 'swimming'],
  [/\b(ruder\w*|aviron|canottaggio|row|rowing)\b/, 'rowing', 'water'],
  [/\b(inline\w*|roller|patinage|pattinaggio)\b/, 'inline_skating'],
  [/\b(crosstrainer|elliptique|ellittica|elliptical)\b/, 'elliptical'],
  [/\b(treppen\w*|stepper|escalier\w*|stair\w*)\b/, 'stair_climbing', 'machine'],
];

const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * Strava's type ("Trail Run", "TrailRun", "EBikeRide", "Lauf", "Course a pied", "Escursione") to our sport, or
 * null: the English names first, then the German, French and Italian names, then whole words.
 */
export function sportFromStrava(type: string): { sport: CardioSport; variant: string } | null {
  const key = fold(type.trim().replace(/([a-z])([A-Z])/g, '$1 $2')).replace(/[-_]/g, ' ').replace(/\s+/g, ' ').replace(/^e(?=bike|mountain)/, 'e ').replace(/^ebike/, 'e bike');
  const hit = STRAVA_TYPES[key] ?? STRAVA_TYPES[key.replace('stairstepper', 'stair stepper')];
  if (hit) return { sport: hit.sport, variant: sportVariant(hit.sport, hit.variant) };
  const local = LOCAL_TYPES[key];
  if (local) return { sport: local[0], variant: sportVariant(local[0], local[1]) };
  if (!key || /weight|yoga|workout|krafttraining|musculation|pesi|alpine|abfahrt|snowboard/.test(key)) return null;
  for (const [re, sport, variant] of TYPE_WORDS) if (re.test(key)) return { sport, variant: sportVariant(sport, variant) };
  return null;
}

/** A CSV file to rows of fields (quotes, doubled quotes and line breaks inside quotes handled). One pass. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; } else if (c === '"') quoted = false; else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows;
}

export interface StravaRow {
  id: string;
  name: string;
  type: string;
  /** Milliseconds since 1970 (Strava writes the date in UTC). */
  startedAt: number | null;
  elapsedSec: number | null;
  movingSec: number | null;
  distanceM: number | null;
  elevationGainM: number | null;
  avgHr: number | null;
  maxHr: number | null;
  /** "activities/123.fit.gz", empty for activities entered by hand on Strava. */
  file: string;
}

/** The columns of activities.csv, by their header words in English, German, French and Italian (accents folded). */
const COLUMNS: Record<keyof Omit<StravaRow, 'distanceM'> | 'distance', RegExp> = {
  id: /^(activity id|aktivitats ?id|id (de l |dell )?activite|id (dell )?attivita|id attivita)$/,
  startedAt: /^(activity date|aktivitatsdatum|datum der aktivitat|date de l activite|data (dell )?attivita)$/,
  name: /^(activity name|aktivitatsname|name der aktivitat|nom de l activite|nome (dell )?attivita)$/,
  type: /^(activity type|aktivitatstyp|aktivitatsart|art der aktivitat|type d activite|type de l activite|tipo (di |dell )?attivita)$/,
  elapsedSec: /^(elapsed time|verstrichene zeit|gesamtzeit|temps ecoule|tempo trascorso)$/,
  movingSec: /^(moving time|bewegungszeit|zeit in bewegung|temps de deplacement|temps en mouvement|tempo in movimento)$/,
  distance: /^(distance|distanz|strecke|distanza)$/,
  elevationGainM: /^(elevation gain|hohenmeter|hohenzunahme|hohenanstieg|gesamtanstieg|denivele positif|gain d altitude|dislivello positivo|guadagno di elevazione)$/,
  avgHr: /^(average heart rate|durchschnittliche herzfrequenz|durchschn herzfrequenz|frequence cardiaque moyenne|frequenza cardiaca media)$/,
  maxHr: /^(max heart rate|maximale herzfrequenz|max herzfrequenz|frequence cardiaque max|frequence cardiaque maximale|frequenza cardiaca massima|frequenza cardiaca max)$/,
  file: /^(filename|dateiname|nom du fichier|nom de fichier|nome (del )?file)$/,
};

const headerKey = (h: string) => fold(h).replace(/[^a-z0-9]+/g, ' ').trim();

/** A number from the CSV: "7.25", "1,234.5", "7,25" (decimal comma), "1.234,5". */
export function csvNumber(raw: string | undefined): number | null {
  const t = (raw ?? '').trim().replace(/['\u2019\s]/g, '');
  if (!t) return null;
  let n: string;
  if (/^-?\d+,\d+$/.test(t)) n = t.replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+,\d+$/.test(t)) n = t.replace(/\./g, '').replace(',', '.');
  else n = t.replace(/,/g, '');
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}

const MONTHS: Record<string, number> = {
  jan: 0, janv: 0, gen: 0, feb: 1, fev: 1, febr: 1, mar: 2, mrz: 2, maer: 2, mars: 2, apr: 3, avr: 3, mai: 4, may: 4, mag: 4,
  jun: 5, juin: 5, giu: 5, jul: 6, juil: 6, lug: 6, aug: 7, aou: 7, aout: 7, ago: 7, sep: 8, sept: 8, set: 8, okt: 9, oct: 9, ott: 9,
  nov: 10, dez: 11, dec: 11, dic: 11,
};

/**
 * Strava's activity date, in UTC: "Oct 10, 2026, 7:00:00 AM" (English), "10.10.2026, 07:00:00", "10 oct. 2026,
 * 07:00:00", "10 ott 2026, 07:00:00". Null when it cannot be read.
 */
export function parseStravaDate(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const direct = Date.parse(/(utc|gmt|z|[+-]\d\d:?\d\d)$/i.test(s) ? s : `${s} UTC`);
  if (Number.isFinite(direct)) return direct;
  const time = /(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?\s*$/i.exec(s);
  let h = time ? Number(time[1]) : 12;
  if (time?.[4]) h = (h % 12) + (time[4].toLowerCase() === 'pm' ? 12 : 0);
  const mi = time ? Number(time[2]) : 0;
  const se = time?.[3] ? Number(time[3]) : 0;
  let m = /^(\d{1,2})[./](\d{1,2})[./](\d{4})/.exec(s);
  if (m) return Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]), h, mi, se);
  m = /^(\d{1,2})\.?\s+([^\s.,\d]+)\.?\s+(\d{4})/.exec(fold(s));
  const month = m ? MONTHS[m[2]!.slice(0, 4)] ?? MONTHS[m[2]!.slice(0, 3)] : undefined;
  if (m && month !== undefined) return Date.UTC(Number(m[3]), month, Number(m[1]), h, mi, se);
  return null;
}

/** What parseStravaCsv found: the rows, and whether the columns could be told apart at all. */
export interface StravaCsv {
  rows: StravaRow[];
  /** False when the headers are in a language this reader does not know (no type or file column). */
  understood: boolean;
}

/** Reads Strava's activities.csv. The first distance column is in km, a later one (newer exports) in m. */
export function parseStravaCsv(text: string): StravaCsv {
  const [head, ...rows] = parseCsv(text.replace(/^\uFEFF/, ''));
  if (!head) return { rows: [], understood: false };
  const keys = head.map(headerKey);
  const all = (re: RegExp) => keys.flatMap((k, i) => (re.test(k) ? [i] : []));
  const col = (name: keyof typeof COLUMNS) => all(COLUMNS[name])[0] ?? -1;
  const at = { id: col('id'), date: col('startedAt'), name: col('name'), type: col('type'), elapsed: col('elapsedSec'), moving: col('movingSec'), gain: col('elevationGainM'), avgHr: col('avgHr'), maxHr: col('maxHr') };
  let file = col('file');
  // The file column can also be found by its values ("activities/123.fit.gz").
  if (file < 0) file = head.findIndex((_, i) => rows.slice(0, 50).some((r) => /^activities\//.test(r[i] ?? '')));
  const dist = all(COLUMNS.distance);
  const understood = at.type >= 0 && at.date >= 0;
  const out: StravaRow[] = [];
  for (const r of rows) {
    const km = csvNumber(r[dist[0] ?? -1]);
    const m = dist.length > 1 ? csvNumber(r[dist[1]!]) : null;
    const pick = (i: number) => (i >= 0 ? csvNumber(r[i]) : null);
    out.push({
      id: (r[at.id] ?? '').trim(),
      name: (r[at.name] ?? '').trim(),
      type: (r[at.type] ?? '').trim(),
      startedAt: parseStravaDate(r[at.date] ?? ''),
      elapsedSec: pick(at.elapsed),
      movingSec: pick(at.moving),
      distanceM: m ?? (km !== null ? km * 1000 : null),
      elevationGainM: pick(at.gain),
      avgHr: pick(at.avgHr),
      maxHr: pick(at.maxHr),
      file: (r[file] ?? '').trim(),
    });
  }
  return { rows: out, understood };
}

/** The activity id of a row ("Activity ID" in activities.csv), for "Open in Strava"; nothing when it is not a Strava number. */
const stravaIdOf = (row: StravaRow): { stravaId?: string } => (/^[1-9]\d{0,19}$/.test(row.id) ? { stravaId: row.id } : {});

/** Strava's own numbers win for the summary (they match what the person sees in Strava); the file gives splits and charts. */
function withStravaTotals(a: CardioActivity, row: StravaRow): CardioActivity {
  const out = { ...a, ...stravaIdOf(row) };
  if (row.movingSec && row.movingSec > 0) out.durationSec = Math.round(row.movingSec);
  if (row.elapsedSec && row.elapsedSec > 0) out.elapsedSec = Math.round(row.elapsedSec);
  if (row.distanceM && row.distanceM > 0) out.distanceM = Math.round(row.distanceM);
  if (row.elevationGainM !== null && row.elevationGainM >= 0 && (a.elevationGainM !== undefined || row.elevationGainM > 0)) out.elevationGainM = Math.round(row.elevationGainM);
  return out;
}

// Duplicates ---------------------------------------------------------------------------------------

/** Two activities are the same when they start within 2 minutes and their distances differ by less than 3 %. */
export function sameActivity(a: { startedAt: number; distanceM?: number }, b: { startedAt: number; distanceM?: number }): boolean {
  if (Math.abs(a.startedAt - b.startedAt) > 120_000) return false;
  if (!a.distanceM || !b.distanceM) return !a.distanceM && !b.distanceM;
  return Math.abs(a.distanceM - b.distanceM) / Math.max(a.distanceM, b.distanceM) < 0.03;
}

/** What the duplicate check needs of an activity in the history. */
export type ExistingActivity = { startedAt: number; activity?: { distanceM?: number; stravaId?: string } };

/**
 * Marks candidates that are already in the history (or twice in the import): the same Strava id, or the same
 * start (2 minutes) and distance (3 %). Two different Strava ids are never the same activity.
 */
export function markDuplicates(candidates: ActivityCandidate[], existing: ExistingActivity[]): ActivityCandidate[] {
  type Seen = { startedAt: number; distanceM?: number; stravaId?: string };
  const seen: Seen[] = existing.filter((s) => s.activity).map((s) => ({
    startedAt: s.startedAt,
    ...(s.activity!.distanceM !== undefined ? { distanceM: s.activity!.distanceM } : {}),
    ...(s.activity!.stravaId ? { stravaId: s.activity!.stravaId } : {}),
  }));
  return candidates.map((c) => {
    if (!c.activity) return c;
    const me: Seen = { startedAt: c.startedAt, ...(c.activity.distanceM !== undefined ? { distanceM: c.activity.distanceM } : {}), ...(c.activity.stravaId ? { stravaId: c.activity.stravaId } : {}) };
    const duplicate = seen.some((s) => (s.stravaId && me.stravaId ? s.stravaId === me.stravaId : sameActivity(s, me)));
    if (!duplicate) seen.push(me);
    return { ...c, duplicate };
  });
}

// The whole import ---------------------------------------------------------------------------------

export interface ImportFile extends BlobLike {
  name: string;
}

export interface ImportOptions {
  maxHr?: number;
  keepRoute?: boolean;
  /** Called after each activity, for a progress line. */
  onProgress?: (done: number, total: number) => void;
}

const TRACK_FILE = /\.(gpx|tcx|fit)(\.gz)?$/i;

/** Shown when activities.csv is in a language this reader does not know. */
export const STRAVA_LANGUAGE_MESSAGE = 'Set Strava to English and export again: the activity list (activities.csv) could not be read in this language. The activity files below are imported with the sport they name, without their Strava names.';

async function fromTrackBytes(file: string, load: () => Promise<Uint8Array>, opts: ImportOptions, strava?: StravaRow): Promise<ActivityCandidate> {
  const fallbackName = strava?.name || file.replace(/^.*\//, '').replace(TRACK_FILE, '');
  try {
    const track = await parseTrackFile(file, await load());
    const sport = strava ? sportFromStrava(strava.type) : null;
    const r = activityFromTrack(track, { maxHr: opts.maxHr, keepRoute: opts.keepRoute, sport, source: strava ? 'strava' : track.format });
    const startedAt = r.startedAt ?? strava?.startedAt ?? null;
    if (startedAt === null) return { file, name: fallbackName, startedAt: 0, activity: null, problem: 'no start time in the file' };
    const sportWord = strava?.type || track.sportHint;
    const activity = r.activity && strava ? withStravaTotals(r.activity, strava) : r.activity;
    return {
      file, name: strava?.name || track.name || fallbackName, startedAt, activity, ...(sportWord ? { sportWord } : {}),
      problem: r.problem, ...(r.sportUnknown ? { sportUnknown: true } : {}),
    };
  } catch (e) {
    return { file, name: fallbackName, startedAt: strava?.startedAt ?? 0, activity: null, problem: e instanceof TrackFileError ? e.message : 'could not be read' };
  }
}

/** A Strava activity entered by hand (no file): its totals from activities.csv. */
function fromStravaRow(row: StravaRow): ActivityCandidate {
  const sport = sportFromStrava(row.type);
  const sec = row.movingSec ?? row.elapsedSec;
  const base = { file: 'activities.csv', name: row.name || row.type, startedAt: row.startedAt ?? 0, sportWord: row.type };
  if (!sport) return { ...base, activity: null, problem: 'not a sport this app tracks yet' };
  if (!row.startedAt || !sec) return { ...base, activity: null, problem: 'no date or time' };
  const activity: CardioActivity = {
    v: 1, sport: sport.sport, variant: sport.variant, durationSec: Math.round(sec),
    ...(row.elapsedSec ? { elapsedSec: Math.round(row.elapsedSec) } : {}),
    ...(row.distanceM ? { distanceM: Math.round(row.distanceM) } : {}),
    ...(row.elevationGainM !== null ? { elevationGainM: Math.round(row.elevationGainM) } : {}),
    ...(row.avgHr ? { avgHr: Math.round(row.avgHr) } : {}),
    ...(row.maxHr ? { maxHr: Math.round(row.maxHr) } : {}),
    source: 'strava',
    ...stravaIdOf(row),
  };
  return { ...base, activity, problem: null };
}

/**
 * Reads the chosen files: single track files, or a Strava export ZIP. Returns one candidate per activity,
 * newest first, with duplicates (in the history or within the import) marked.
 */
export async function importActivityFiles(files: ImportFile[], existing: ExistingActivity[], opts: ImportOptions = {}): Promise<ActivityCandidate[]> {
  const out: ActivityCandidate[] = [];
  for (const f of files) {
    if (/\.zip$/i.test(f.name)) {
      out.push(...await importStravaZip(f, opts));
    } else if (TRACK_FILE.test(f.name)) {
      out.push(await fromTrackBytes(f.name, async () => new Uint8Array(await f.slice(0, f.size).arrayBuffer()), opts));
      opts.onProgress?.(out.length, files.length);
    } else {
      out.push({ file: f.name, name: f.name, startedAt: 0, activity: null, problem: 'only GPX, TCX, FIT or a Strava export ZIP can be imported' });
    }
  }
  return markDuplicates(out.sort((a, b) => b.startedAt - a.startedAt), existing);
}

/**
 * Strava's account export: activities.csv for names, types and dates, the files in activities/. At most
 * 25 MB unpacked per file and 2 GB for the whole export.
 */
export async function importStravaZip(zip: BlobLike, opts: ImportOptions = {}): Promise<ActivityCandidate[]> {
  const entries = await zipEntries(zip);
  let budget = MAX_ZIP_TOTAL_BYTES;
  const read = async (e: ZipEntry, max = MAX_UNPACKED_BYTES) => {
    if (budget <= 0) throw new TrackFileError('This export unpacks to more than 2 GB; import it in parts (unpack it and choose the activity files).');
    const bytes = await zipRead(zip, e, Math.min(max, budget));
    budget -= bytes.length;
    return bytes;
  };
  const byName = new Map(entries.map((e) => [e.name.replace(/^[^/]*\/(?=activities\/)/, ''), e]));
  const csvEntry = entries.find((e) => /(^|\/)activities\.csv$/i.test(e.name));
  const csv = csvEntry ? parseStravaCsv(new TextDecoder().decode(await read(csvEntry, 50_000_000))) : { rows: [], understood: true };
  const rows = csv.understood ? csv.rows : [];
  const out: ActivityCandidate[] = [];
  if (!csv.understood) out.push({ file: 'activities.csv', name: 'Activity list', startedAt: Number.MAX_SAFE_INTEGER, activity: null, problem: STRAVA_LANGUAGE_MESSAGE, notice: true });
  const used = new Set<string>();
  const total = rows.length || entries.filter((e) => TRACK_FILE.test(e.name)).length;
  let done = 0;
  for (const row of rows) {
    const entry = row.file ? byName.get(row.file) : undefined;
    if (!sportFromStrava(row.type) && !entry) {
      out.push({ file: row.file || 'activities.csv', name: row.name || row.type, startedAt: row.startedAt ?? 0, activity: null, sportWord: row.type, problem: 'not a sport this app tracks yet' });
    } else if (entry) {
      used.add(entry.name);
      const c = await fromTrackBytes(entry.name, () => read(entry), opts, row);
      // A type this reader does not know (another language): the file's own sport, or the person picks one.
      out.push(c);
    } else {
      out.push(fromStravaRow(row));
    }
    opts.onProgress?.(++done, total);
  }
  // Without activities.csv (or files it does not list): every track file on its own.
  const listed = new Set(rows.map((r) => r.file).filter(Boolean));
  for (const e of entries) {
    // byName keys are "activities/123.fit.gz", the way activities.csv lists them: one lookup per entry.
    const key = e.name.replace(/^[^/]*\/(?=activities\/)/, '');
    if (used.has(e.name) || !TRACK_FILE.test(e.name) || listed.has(key)) continue;
    out.push(await fromTrackBytes(e.name, () => read(e), opts));
    opts.onProgress?.(++done, total);
  }
  if (!csvEntry && out.length === 0) throw new TrackFileError('This ZIP has no activities.csv and no GPX, TCX or FIT files.');
  return out;
}
